import { AuthProfile } from '@r123/auth';

import {
    BasicTierConstant,
    FeaturedTierConstant,
    GTSTierConstant,
    PremierTierConstant,
} from '@/app/_constant/subscription-tier.constant';
import { PrimaryProject } from '@/modules/core/gql/generated';
import gtag from '@/modules/gtag/gtag';
import { PropertyTypeEnumToText } from '@/modules/property/property.helper';
import {
    ListingTypeEnum,
    PropertyTypeEnum,
} from '@/modules/property/property.type';

import { ListingDetail } from '../[lang]/(listing)/properti/_presentation/type';
import { AuthUserTypeEnum } from '../_constant/authType';
import {
    FEATURE_BEFORE_KEY,
    FIRST_VIEW_URL_KEY,
    LANDING_URL_SESSION_KEY,
    PAGE_BEFORE_KEY,
} from '../_helper/url';
import {
    GA4EventActionEnum,
    GA4FeatureType,
    GA4GTMParamInterface,
    GA4ListingParam,
    GA4PageParam,
    GA4PageType,
    GA4SellerParam,
    GA4UserParam,
} from './GA4TrackerType';

export const sendGA4Tracker = ({
    eventAction,
    pageType,
    featureType,
    listing,
    authData,
    token,
    position,
}: GA4ParamInterface) => {
    try {
        const params: GA4GTMParamInterface = {
            event: 'r123BaseTrigger', // event trigger is hardcoded
            eventAction,
            page: generatePage(featureType, pageType),
            listing: listing ? generateListing(listing, position) : undefined,
            // search: TBD, // TODO: implement later for SRP
            seller: listing ? generateSeller(listing) : undefined,
            user: generateUser(token, authData),
        };

        gtag.dataLayerPush(params);
    } catch (e) {
        console.debug(`[GA4 Tracker] - ${e}`);
    }
};

const generateListing = (
    listing?: ListingDetail,
    position?: number,
): GA4ListingParam => {
    try {
        /**
         * Notes: Hardcoded only for LDP
         * Please adjust the logic during UDP & PDP migration
         */
        const isPrimary = false;
        const marketType = 'Secondary'; // TODO: Please adjust after migrate UDP & PDP
        const propertyType = PropertyTypeEnumToText(
            listing?.listingType ?? PropertyTypeEnum.allResidential,
            'en',
        );

        const priceType = listing?.priceType ?? 0;
        const channel =
            `${priceType}` === `${ListingTypeEnum.sale}` ? 'Sale' : 'Rent';
        const marketLabel = isPrimary
            ? 'Primary Project'
            : `${marketType} ${propertyType} for ${channel}`;

        const productName = translateProjectStatus(
            listing?.status ?? 1,
            listing?.subscription?.id ?? BasicTierConstant,
            priceType,
            isPrimary,
            null, // TODO: add when PDP come
        );

        return {
            area: null, // put null because API not Supported
            cardPosition: position ? position + 1 : 1,
            id: listing?.originId ?? '',
            locationLevel1: listing?.location?.province.name ?? '',
            locationLevel2: listing?.location?.city.name ?? '',
            locationLevel3: listing?.location?.district.name ?? '',
            marketType: marketLabel,
            productName,
        };
    } catch (e) {
        console.debug(`[GA4 Generate Listing] - ${e}`);

        return {
            area: '',
            cardPosition: 0,
            id: '',
            locationLevel1: '',
            locationLevel2: '',
            locationLevel3: '',
            marketType: '',
            productName: '',
        };
    }
};

const generatePage = (
    featureType: GA4FeatureType | undefined,
    pageType: GA4PageType,
): GA4PageParam => {
    try {
        const featureBeforeStorage =
            window.sessionStorage.getItem(FEATURE_BEFORE_KEY);
        const pageBeforeStorage =
            window.sessionStorage.getItem(PAGE_BEFORE_KEY);

        let onePageFeatureBefore: string | null = null;
        let twoPageFeatureBefore: string | null = null;
        let onePageBefore: string | null = null;
        let twoPageBefore: string | null = null;

        if (featureBeforeStorage !== null) {
            const arrFeatureBefore = JSON.parse(
                featureBeforeStorage,
            ) as string[];

            onePageFeatureBefore =
                arrFeatureBefore.length >= 2 ? arrFeatureBefore[1] : null;
            twoPageFeatureBefore =
                arrFeatureBefore.length >= 3 ? arrFeatureBefore[2] : null;
        }

        if (pageBeforeStorage !== null) {
            const arrPageBefore = JSON.parse(pageBeforeStorage) as string[];

            onePageBefore = arrPageBefore.length >= 2 ? arrPageBefore[1] : null;
            twoPageBefore = arrPageBefore.length >= 2 ? arrPageBefore[2] : null;
        }

        return {
            feature: featureType || '',
            onePageBefore,
            onePageFeatureBefore,
            twoPageBefore,
            twoPageFeatureBefore,
            type: pageType,
        };
    } catch (e) {
        console.error(`[GA4 Generate Page] - ${e}`);

        return {
            feature: featureType || '',
            onePageBefore: '',
            onePageFeatureBefore: '',
            twoPageBefore: '',
            twoPageFeatureBefore: '',
            type: pageType,
        };
    }
};

const translateProjectStatus = (
    listingStatus: number,
    subscriptionTierId: number,
    priceType: number,
    isPrimary: boolean,
    primaryProject?: PrimaryProject | null,
): string => {
    try {
        let boosterName = 'Basic';
        const boosterPrefix =
            isPrimary || primaryProject !== null ? 'Primary' : 'Secondary';
        const rentSoldSuffix =
            priceType === ListingTypeEnum.sale ? 'Sold' : 'Rented';

        // Primary Booster
        if (primaryProject) {
            if (primaryProject.projectType === 5) boosterName = 'PASTI';
            if (primaryProject.projectType === 4) boosterName = 'LGP+';
            if (primaryProject.projectType === 3) boosterName = 'LGP';
            if (
                primaryProject.projectType === 1 &&
                subscriptionTierId === PremierTierConstant
            )
                boosterName = 'Premier';

            if (
                primaryProject.projectType === 1 &&
                subscriptionTierId === FeaturedTierConstant
            )
                boosterName = 'Featured';

            if (listingStatus === 5) boosterName = 'Evergreen';
        }

        // Secondary Booster
        if (!primaryProject) {
            switch (subscriptionTierId) {
                case PremierTierConstant:
                    boosterName = 'Premier';
                    break;
                case FeaturedTierConstant:
                    boosterName = 'Featured';
                    break;
                case GTSTierConstant:
                    boosterName = 'TopProperty';
                    break;
                default:
                    boosterName = 'Basic';
                    break;
            }

            if (listingStatus === 5 && boosterName === 'Basic')
                boosterName = rentSoldSuffix;

            if (listingStatus === 5 && boosterName !== 'Basic')
                boosterName += rentSoldSuffix;
        }

        return `${boosterPrefix}${boosterName}`;
    } catch (e) {
        console.debug(`[GA4 Translate Project Status] - ${e}`);

        return '';
    }
};

const generateSeller = (listing?: ListingDetail): GA4SellerParam => {
    try {
        const company = listing?.agent?.agencyName ?? '';
        const sellerId = listing?.agent?.originId;
        const sellerName = listing?.agent?.name;
        const subscriptionName = listing?.agent?.subscription?.name;
        const info = `${sellerId} - ${sellerName} - Membership ${subscriptionName}`;

        return {
            company,
            info,
        };
    } catch (e) {
        console.error(`[GA4 Generate Seller] - ${e}`);

        return {
            company: '',
            info: '',
        };
    }
};

const generateUser = (token: string, authData?: AuthProfile): GA4UserParam => {
    try {
        const acquisitionUrl = window.localStorage.getItem(FIRST_VIEW_URL_KEY);

        const landingUrl = window.sessionStorage.getItem(
            LANDING_URL_SESSION_KEY,
        );

        return {
            acquisitionUrl: acquisitionUrl || '',
            email: authData?.email ?? '',
            landingUrl: landingUrl || '',
            location: null, // not included on r123 portal
            name: authData?.name ?? '',
            numberOfVisit: '', // TODO: find out get from which data ,
            phoneNumber: authData?.phone_number ?? '',
            sessionIdentifier: token,
            type: generateUserType(authData),
        };
    } catch (e) {
        console.error(`[GA4 Generate User] - ${e}`);

        return {
            acquisitionUrl: '',
            email: '',
            landingUrl: '',
            location: '',
            name: '',
            numberOfVisit: '',
            phoneNumber: '',
            sessionIdentifier: '',
            type: '',
        };
    }
};

const generateUserType = (authData?: AuthProfile): string => {
    try {
        let userType: string = authData
            ? 'User Registered Logged In'
            : 'User Unregistered';

        if (authData?.user_type == AuthUserTypeEnum.agent) {
            userType = 'Registered';
        }

        return userType;
    } catch (e) {
        console.error(`[GA4 Generate User Type] - ${e}`);

        return '';
    }
};

export interface GA4ParamInterface {
    eventAction: GA4EventActionEnum;
    pageType: GA4PageType;
    featureType: GA4FeatureType;
    listing?: ListingDetail;
    authData?: AuthProfile;
    token: string;
    position?: number;
    target?: string;
}
